// The JSON API under /api (see the README's "Web API"). Message ids go in the query string or the body, never in
// the path (Message-IDs may contain '/').

import { API_BASE, SignedOut, accessToken } from "./auth";
import { NetworkError, reportAnswer } from "./net";

export interface LastSync {
  synced_at: string | null;
  run_ok: boolean | null;
  run_error: string | null;
}

export interface Folder {
  name: string;
  messages: number;
  unread: number;
  size_bytes: number;
  in_yahoo: number | null;
  last_synced_at: string | null;
  last_error: string | null;
  backfill_done: boolean;
  change: number;
  changed_at: string | null;
  /** The newest mail the folder held ("<uidvalidity>:<uid>"; null before its first sync): marking it read sends it, so
   * the mail that came after stays unread however late the request is done. */
  upto: string | null;
}

export interface FoldersResponse {
  folders: Folder[];
  last_sync: LastSync;
}

export interface MessageRow {
  id: string;
  date: string | null;
  from_addr: string | null;
  from_name: string | null;
  subject: string | null;
  snippet: string;
  folders: string[];
  has_attachments: boolean;
  /** The attachments' file names, comma-separated. */
  attachment_names: string | null;
  matched_attachment: string | null;
  deleted_at: string | null;
  /** The address the message was sent to, from its delivery headers (null: none, or not read yet). */
  sent_to: string | null;
  flags: string[];
  unread: boolean;
  flagged: boolean;
}

export interface MessagesResponse {
  total: number;
  capped: boolean;
  offset: number;
  limit: number;
  messages: MessageRow[];
  last_sync: LastSync;
}

export interface Attachment {
  part_index: number;
  filename: string | null;
  mime: string | null;
  size: number | null;
  pages: number | null;
  ocr_status: string | null;
  has_text: boolean;
  inline: boolean;
}

/** One hop of a message's way to the mailbox: an address it was delivered to, and the headers that say so. */
export interface DeliveryStep {
  address: string;
  sources: string[];
  note: string | null;
}

/** Why a message is in the mailbox (null for mail sent from the account): the address the sender used, then each
 * forward, last the mailbox. `addressed`: that first address is in To or Cc (false: Bcc, a list, most spam). */
export interface Delivery {
  sent_to: string;
  addressed: boolean;
  forwarded: boolean;
  summary: string;
  path: DeliveryStep[];
}

export interface Message {
  id: string;
  subject: string | null;
  from_addr: string | null;
  from_name: string | null;
  to: string[] | null;
  cc: string[] | null;
  /** The Reply-To addresses: where a reply goes instead of the sender. */
  reply_to: string[];
  date: string | null;
  folders: { folder: string; flags: string[] }[];
  flags: string[];
  unread: boolean;
  flagged: boolean;
  deleted_at: string | null;
  in_reply_to: string | null;
  references: string[] | null;
  size_bytes: number | null;
  has_attachments: boolean;
  attachments: Attachment[];
  body_text: string | null;
  html: string | null;
  delivery: Delivery | null;
}

export interface ThreadRow {
  id: string;
  date: string | null;
  from_addr: string | null;
  from_name: string | null;
  to: string[] | null;
  subject: string | null;
  snippet: string;
  folders: string[];
  has_attachments: boolean;
  deleted_at: string | null;
}

export interface SyncResult {
  folder: string;
  checked: boolean;
  changed: boolean;
  new_messages: number;
  busy: boolean;
  truncated: boolean;
  errors: string[];
  synced_at: string | null;
}

/** The answer of a move, trash, read flag or star. `skipped`: a move or trash from a folder the message is no longer in, so
 * nothing was done (sent again after a lost answer, or another device was first). */
export type ActionResult = Record<string, unknown> & { skipped?: string };

/** The most messages one action on several takes (the server's limit). */
export const BULK_MAX = 500;

/** What became of one message in an action on several: done; skipped (nothing was done, `reason` says why: it was no
 * longer in the folder it was moved from); failed (`reason` what went wrong: its folder gone or reset, Yahoo's refusal). */
export interface BulkOutcome {
  id: string;
  status: "done" | "skipped" | "failed";
  reason?: string;
}

/** The answer of an action on several messages: an outcome per message, in the order asked. */
export interface BulkResult {
  results: BulkOutcome[];
  recorded?: boolean;
}

/** POST /api/send. Attachments are files from the device (base64) or attachments of archived messages. */
export interface SendRequest {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  reply_to?: string;
  forward_of?: string;
  attachments?: ({ filename: string; mime: string; data: string } | { message_id: string; part: number })[];
  /** Made once per message and sent with every retry of it: the server sends a message once. */
  idempotency_key: string;
}

export interface SendResult {
  message_id: string;
  /** The copy Yahoo files in Sent is in the archive already. */
  archived: boolean;
  /** A retry of a message sent before: nothing was sent now. */
  already_sent: boolean;
}

/** A forwarding rule: new mail whose sender address contains `from` and/or whose subject contains `subject` (ignoring
 * case) is forwarded to `to`. */
export interface ForwardRule {
  id: number;
  name: string | null;
  from: string | null;
  subject: string | null;
  to: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
  forwarded: number;
  pending: number;
  /** Forwards given up after failing. */
  failed: number;
  last_forwarded_at: string | null;
}

/** The fields of a new rule, or the ones to change; "" or null clears name, from and subject. */
export interface RuleFields {
  name?: string | null;
  from?: string | null;
  subject?: string | null;
  to?: string[];
  enabled?: boolean;
}

export interface RuleMatch {
  id: string;
  date: string | null;
  from_addr: string | null;
  from_name: string | null;
  subject: string | null;
  folders: string[];
  deleted_at: string | null;
}

/** The archived messages a rule's conditions match (shown, never forwarded). */
export interface RulePreview {
  total: number;
  messages: RuleMatch[];
}

export interface ForwardLogEntry {
  rule_id: number;
  rule_name: string | null;
  message_id: string;
  date: string | null;
  from_addr: string | null;
  from_name: string | null;
  subject: string | null;
  folder: string;
  to: string[];
  status: "pending" | "sent" | "failed" | "skipped";
  attempts: number;
  error: string | null;
  next_attempt_at: string;
  created_at: string;
  sent_at: string | null;
  sent_message_id: string | null;
}

/** An address to suggest when writing mail (GET /api/contacts, best first). */
export interface Contact {
  addr: string;
  /** The latest From name of the address; null when it never sent mail. */
  name: string | null;
  /** Messages I sent to it and messages it sent me. */
  sent: number;
  received: number;
  /** The date of the latest of them. */
  last: string | null;
}

/** The web app's settings (GET / PUT /api/settings), kept on the server so they survive reinstalling the app. A PUT
 * changes the settings it holds and leaves the others. */
export interface AppSettings {
  /** The folders kept on the device for reading offline, each with or without its attachments. */
  offline_folders: OfflineFolder[];
  /** The folders new mail is notified in (default Inbox). Missing from settings kept by an older version. */
  push_folders?: string[];
}

/** A device new-mail notifications go to (GET /api/push). */
export interface PushDevice {
  /** The first 16 hex digits of the SHA-256 of its push endpoint (push.ts deviceId). */
  id: string;
  name: string | null;
  /** The push service's host (web.push.apple.com, fcm.googleapis.com, ...). */
  service: string | null;
  created_at: string;
  /** When the app last registered it. */
  updated_at: string;
  last_sent_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  /** Failed pushes in a row (0: the last one went through). */
  failures: number;
}

export interface PushInfo {
  /** The server's VAPID key (base64url): the applicationServerKey to subscribe with. */
  public_key: string;
  devices: PushDevice[];
}

export interface OfflineFolder {
  folder: string;
  attachments: boolean;
}

/** Every message in a folder, newest first, for the offline copy (GET /api/offline/folder). */
export interface FolderListing {
  folder: string;
  /** The folder's change marker when it was listed (as in /api/folders). */
  change: number;
  messages: { id: string; date: string | null; flags: string[] }[];
}

/** A message for the offline copy: as GET /api/message gives it, with its listing's snippet. */
export type OfflineMessage = Message & { snippet: string };

/** POST /api/offline/messages: the messages in the order asked; `missing` are not in the archive (or could not be
 * read), `more` were left for the next request (the answer stops at about 8 MB of archived mail). */
export interface OfflineBatch {
  messages: OfflineMessage[];
  missing: string[];
  more: string[];
}

export interface Me {
  user: { upn?: string; name?: string; oid?: string } | null;
  yahoo_user: string;
  auth: string;
}

export interface SearchParams {
  q?: string;
  folder?: string;
  from?: string;
  to?: string;
  after?: string;
  before?: string;
  has_attachment?: boolean;
  include_deleted?: boolean;
  /** The address the message was sent to (its delivery headers): a whole one matches only itself, a part such as
   * "@mydomain.dk" every address containing it. */
  sent_to?: string;
  /** Not the mail in Trash and no other folder (mail no longer in Yahoo is in none: include_deleted decides it). */
  exclude_trash?: boolean;
  /** Only starred mail (IMAP's \Flagged): in `folder`, or in any folder but Trash and spam (Yahoo's Starred). */
  flagged?: boolean;
  /** The words as written, not their other forms ("faktura" is not "fakturaen"), case aside; every message containing
   * them is listed, as the server's keyword mode. */
  exact?: boolean;
  sort?: "relevance" | "newest";
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** 409 from /api/send: "sending", "uncertain" or "sent_already". */
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Set once at start-up when the server runs with AUTH_MODE=none (local development): no tokens at all. */
let openServer = false;
export function setOpenServer(open: boolean): void {
  openServer = open;
}

/** How long a request may go unanswered before it counts as no answer (NetworkError: an action then waits in the
 * outbox and is sent again, which is safe): a page frozen in the background on a phone can come back to a request
 * that never ends, and every action behind it would wait for it. Sending mail takes as long as its files take to
 * upload. A request with a signal of its own (a download) is left to it. */
const REQUEST_TIMEOUT_MS = 60_000;
const SEND_TIMEOUT_MS = 5 * 60_000;

function timeoutSignal(ms: number): AbortSignal | null {
  return typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(ms) : null;
}

async function send(path: string, init: RequestInit = {}, retried = false): Promise<Response> {
  const headers = new Headers(init.headers);
  const timeout = init.signal ? null : timeoutSignal(path.startsWith("/send") ? SEND_TIMEOUT_MS : REQUEST_TIMEOUT_MS);
  let res: Response;
  try {
    if (!openServer) {
      const token = await accessToken(retried);
      if (!token) throw new SignedOut();
      headers.set("Authorization", `Bearer ${token}`);
    }
    res = await fetch(`${API_BASE}/api${path}`, { ...init, headers, signal: init.signal ?? timeout ?? undefined });
  } catch (e) {
    if (timeout?.aborted) {
      reportAnswer(false);
      throw new NetworkError("The server did not answer in time");
    }
    // fetch() (here or renewing the token) rejects with a TypeError when no answer came
    if (!(e instanceof TypeError)) throw e;
    reportAnswer(false);
    throw new NetworkError();
  }
  reportAnswer(true);
  if (res.status === 401 && !openServer) {
    if (!retried) return send(path, init, true); // revoked or expired early: renew once
    throw new SignedOut();
  }
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let code: string | null = null;
    try {
      const body = (await res.json()) as { error?: string; code?: string };
      message = body.error ?? message;
      code = body.code ?? null;
    } catch {
      // not JSON (a gateway error page)
    }
    throw new ApiError(message, res.status, code);
  }
  return res;
}

/** A file download followed (the bytes received so far) and stopped when it is not wanted any more. */
export interface Fetching {
  progress?: (received: number) => void;
  signal?: AbortSignal;
}

/** The body of `res` read piece by piece, telling `progress` the bytes received after each. */
export async function readBody(res: Response, progress: (received: number) => void): Promise<Blob> {
  if (!res.body) return res.blob();
  const reader = res.body.getReader();
  const chunks: BlobPart[] = [];
  let received = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      progress(received);
    }
  } catch (e) {
    if (e instanceof TypeError) throw new NetworkError(); // the connection broke off
    throw e;
  }
  return new Blob(chunks, { type: res.headers.get("Content-Type") ?? "" });
}

async function get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) if (v !== undefined && v !== "") query.set(k, String(v));
  const qs = query.toString();
  return (await send(qs ? `${path}?${qs}` : path)).json() as Promise<T>;
}

async function write<T>(method: "POST" | "PUT" | "DELETE", path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  return (await send(path, init)).json() as Promise<T>;
}

const post = <T>(path: string, body: unknown) => write<T>("POST", path, body);

/** /api/me without a token: answers only when the server is open (AUTH_MODE=none). */
export async function probeOpenServer(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/me`);
    if (!res.ok) return false;
    return ((await res.json()) as Me).auth === "none";
  } catch {
    return false;
  }
}

export const api = {
  me: () => get<Me>("/me"),
  folders: () => get<FoldersResponse>("/folders"),
  messages: (params: SearchParams & { limit?: number; offset?: number }) => get<MessagesResponse>("/messages", { ...params }),
  message: (id: string) => get<Message>("/message", { id }),
  thread: (id: string) => get<{ id: string; messages: ThreadRow[] }>("/message/thread", { id }),
  /** `progress`: told the bytes received so far as the file comes in; `signal`: stops it (an AbortError). */
  attachment: async (id: string, part: number, { progress, signal }: Fetching = {}): Promise<Blob> => {
    const res = await send(`/message/attachment?${new URLSearchParams({ id, part: String(part) })}`, { signal });
    return progress ? readBody(res, progress) : res.blob();
  },
  /** A link of a few minutes to a copy of the attachment served as its type (for Wallet, Calendar and Contacts). */
  attachmentLink: async (id: string, part: number, signal?: AbortSignal): Promise<{ url: string; mime: string }> =>
    (await send(`/message/attachment/link?${new URLSearchParams({ id, part: String(part) })}`, { signal })).json(),
  /** `from`: the folder the message is moved from, and only while it is still there (else `skipped` says why). */
  move: (id: string, folder: string, from?: string) => post<ActionResult>("/message/move", { id, folder, from }),
  trash: (id: string, from?: string) => post<ActionResult>("/message/trash", { id, from }),
  read: (id: string, read: boolean) => post<ActionResult>("/message/read", { id, read }),
  /** The star (IMAP's \Flagged) set or cleared in every folder the message is in. */
  flag: (id: string, flagged: boolean) => post<ActionResult>("/message/flag", { id, flagged }),
  /** The same on several messages (BULK_MAX at most), in one IMAP session, an outcome for each: `from` as for one. */
  moveMany: (ids: string[], folder: string, from?: string) => post<BulkResult>("/messages/move", { ids, folder, from }),
  trashMany: (ids: string[], from?: string) => post<BulkResult>("/messages/trash", { ids, from }),
  readMany: (ids: string[], read: boolean) => post<BulkResult>("/messages/read", { ids, read }),
  flagMany: (ids: string[], flagged: boolean) => post<BulkResult>("/messages/flag", { ids, flagged }),
  /** Every unread message in the folder marked read; `marked`: how many Yahoo had unread. */
  markFolderRead: (folder: string, upto: string | null) => post<{ folder: string; marked: number }>("/folder/read", { folder, upto }),
  restore: (id: string, folder: string) => post<Record<string, unknown>>("/message/restore", { id, folder }),
  sync: (folder: string) => post<SyncResult>("/sync", { folder }),
  send: (message: SendRequest) => post<SendResult>("/send", message),
  forwardRules: () => get<{ rules: ForwardRule[] }>("/forward-rules"),
  createRule: (fields: RuleFields) => post<ForwardRule>("/forward-rules", fields),
  updateRule: (id: number, fields: RuleFields) => write<ForwardRule>("PUT", `/forward-rules?id=${id}`, fields),
  deleteRule: (id: number) => write<{ id: number; deleted: boolean }>("DELETE", `/forward-rules?id=${id}`),
  previewRule: (from: string, subject: string, limit = 10) => get<RulePreview>("/forward-rules/preview", { from, subject, limit }),
  forwardLog: (rule: number, limit = 20) => get<{ forwards: ForwardLogEntry[] }>("/forward-rules/log", { rule, limit }),
  contacts: () => get<{ contacts: Contact[] }>("/contacts"),
  settings: () => get<AppSettings>("/settings"),
  saveSettings: (settings: Partial<AppSettings>) => write<AppSettings>("PUT", "/settings", settings),
  offlineFolder: (folder: string) => get<FolderListing>("/offline/folder", { folder }),
  offlineMessages: (ids: string[]) => post<OfflineBatch>("/offline/messages", { ids }),
  push: () => get<PushInfo>("/push"),
  addPushDevice: (subscription: PushSubscriptionJSON, name: string) => post<PushDevice>("/push/devices", { subscription, name }),
  removePushDevice: (id: string) => write<{ id: string; removed: boolean }>("DELETE", `/push/devices?id=${encodeURIComponent(id)}`),
  testPush: (id: string) => post<{ id: string; sent: boolean }>("/push/test", { id }),
};
